import { useState, useEffect, useCallback, useRef } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import {
  Plus, Save, Trash2, Loader2, Camera, AlertTriangle,
  ChevronLeft, ChevronRight, CheckCircle2, Clock, XCircle, Archive, Flag
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import { VoiceButton } from "@/components/VoiceButton";
import { useOffline } from "@/lib/OfflineContext";
import { queueAction, storePhoto, getPhotosForTask } from "@/lib/offlineDB";
import { useLongPress } from "@/hooks/useLongPress";
import { useSpeechRecognition } from "@/hooks/useSpeechRecognition";

const LOTS = [
  { key: "avancement_global", label: "Avancement global", short: "Avanc." },
  { key: "moa", label: "M.O.A", short: "MOA" },
  { key: "moe", label: "M.O.E", short: "MOE" },
  { key: "gros_oeuvre_vrd", label: "Gros Œuvre / VRD", short: "GO/VRD" },
  { key: "charpente_couverture", label: "Charpente & Couverture", short: "Charp." },
  { key: "basse_tension", label: "Basse Tension", short: "BT" },
  { key: "electricite", label: "Electricité", short: "Elec." },
  { key: "plomberie", label: "Plomberie", short: "Plomb." },
  { key: "aluminium", label: "Aluminium", short: "Alu." },
  { key: "etancheite_peinture", label: "Etanchéité / Peinture", short: "Et./Pei." },
  { key: "enduit", label: "Enduit", short: "Enduit" },
  { key: "carrelage", label: "Carrelage", short: "Carrel." },
  { key: "cuisiniste", label: "Cuisiniste", short: "Cuisine" },
  { key: "menuiserie_bois_placo", label: "Menuiserie Bois + Placo", short: "Men.B." },
];

const STATUTS = [
  { value: "en_cours", label: "En cours", color: "text-blue-400 bg-blue-500/15", selectColor: "#60a5fa" },
  { value: "realise", label: "Réalisé", color: "text-emerald-400 bg-emerald-500/15", selectColor: "#34d399" },
  { value: "a_refaire", label: "À refaire", color: "text-red-400 bg-red-500/15", selectColor: "#f87171" },
  { value: "archive", label: "Non réalisé", color: "text-red-700 bg-red-900/20", selectColor: "#b91c1c" },
];

// Offline photo thumbnail component
const OfflinePhotoThumb = ({ photo }) => {
  const [thumbUrl, setThumbUrl] = useState(null);
  useEffect(() => {
    if (photo.blob) {
      const url = URL.createObjectURL(photo.blob);
      setThumbUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [photo.blob]);
  return (
    <div className="w-8 h-8 rounded border border-orange-500/50 overflow-hidden relative" data-testid={`offline-photo-${photo.id}`}>
      {thumbUrl ? (
        <img src={thumbUrl} alt="" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full bg-zinc-800 flex items-center justify-center">
          <Camera size={10} className="text-zinc-600" />
        </div>
      )}
      <div className="absolute inset-0 bg-black/40 flex items-center justify-center" title="En attente de sync">
        <span className="text-[8px]">&#9203;</span>
      </div>
    </div>
  );
};

const SuiviLotsPanel = ({ crId, readOnly = false }) => {
  const [activeLot, setActiveLot] = useState(() => {
    try { return sessionStorage.getItem(`cr-tab-${crId}`) || LOTS[0].key; } catch { return LOTS[0].key; }
  });
  const [taches, setTaches] = useState([]);
  const [moaTaches, setMoaTaches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null);
  const [autoSaving, setAutoSaving] = useState(null);
  const [savedRecently, setSavedRecently] = useState(null);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const [uploadingFor, setUploadingFor] = useState(null);
  const tachesRef = useRef([]);
  const debounceTimers = useRef({});
  const { isOffline, refreshCounts } = useOffline();
  const [offlinePhotosMap, setOfflinePhotosMap] = useState({});
  const [lotCounts, setLotCounts] = useState({});
  const [animatingNew, setAnimatingNew] = useState(null);
  const [archivingId, setArchivingId] = useState(null);
  const touchStartX = useRef(0);
  const newTaskRef = useRef(null);
  const [longPressTooltip, setLongPressTooltip] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);

  // Save active lot to sessionStorage
  useEffect(() => {
    try { sessionStorage.setItem(`cr-tab-${crId}`, activeLot); } catch { /* ignore */ }
  }, [activeLot, crId]);

  const loadOfflinePhotos = useCallback(async (taskList) => {
    const map = {};
    for (const t of taskList) {
      const offPhotos = await getPhotosForTask(t.id);
      if (offPhotos.length > 0) {
        map[t.id] = offPhotos;
      }
    }
    setOfflinePhotosMap(map);
  }, []);

  const loadTaches = useCallback(async (lot) => {
    setLoading(true);
    try {
      const res = await api.get(`/comptes-rendus/${crId}/taches?lot=${lot}`);
      setTaches(res.data);
      // Load MOA report tasks for non-MOA lots
      if (lot !== "moa" && lot !== "avancement_global") {
        const moaRes = await api.get(`/comptes-rendus/${crId}/taches-moa?lot_concerne=${lot}`);
        setMoaTaches(moaRes.data);
      } else {
        setMoaTaches([]);
      }
      // Load offline photos for visible tasks
      loadOfflinePhotos(res.data);
    } catch (e) {
      toast.error("Erreur chargement tâches");
    } finally {
      setLoading(false);
    }
  }, [crId, loadOfflinePhotos]);

  useEffect(() => { loadTaches(activeLot); }, [activeLot, loadTaches]);

  // Keep ref in sync for debounce callbacks
  useEffect(() => { tachesRef.current = taches; }, [taches]);

  // Cleanup debounce timers on lot change or unmount
  useEffect(() => {
    return () => {
      Object.values(debounceTimers.current).forEach(clearTimeout);
      debounceTimers.current = {};
    };
  }, [activeLot]);

  // Load task counts per lot for badges
  useEffect(() => {
    const loadCounts = async () => {
      try {
        const res = await api.get(`/comptes-rendus/${crId}/taches`);
        const counts = {};
        for (const t of res.data) {
          if (t.statut !== "archive" && t.statut !== "realise") {
            counts[t.lot] = (counts[t.lot] || 0) + 1;
          }
        }
        setLotCounts(counts);
      } catch { /* ignore */ }
    };
    loadCounts();
  }, [crId, taches]); // Refresh counts when taches change

  // Swipe handler for lot navigation on mobile
  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback((e) => {
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(deltaX) < 60) return; // Too short
    const idx = LOTS.findIndex(l => l.key === activeLot);
    if (deltaX < 0 && idx < LOTS.length - 1) {
      setActiveLot(LOTS[idx + 1].key);
    } else if (deltaX > 0 && idx > 0) {
      setActiveLot(LOTS[idx - 1].key);
    }
  }, [activeLot]);

  const addTache = async () => {
    if (isOffline) {
      toast.info("Création de tâche impossible hors-ligne");
      return;
    }
    try {
      const res = await api.post(`/comptes-rendus/${crId}/taches`, {
        lot: activeLot,
        description: "",
        statut: "en_cours"
      });
      setAnimatingNew(res.data.id);
      // Insert new task at top of local state immediately
      setTaches(prev => [res.data, ...prev.map(t => ({ ...t, ordre: (t.ordre ?? 0) + 1 }))]);
      // Scroll to top and auto-focus the description
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="tache-${res.data.id}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        const textarea = document.querySelector(`[data-testid="tache-desc-${res.data.id}"]`);
        if (textarea) textarea.focus();
        setTimeout(() => setAnimatingNew(null), 400);
      }, 100);
    } catch (e) {
      toast.error("Erreur création tâche");
    }
  };

  const _buildPayload = (t) => ({
    description: t.description ?? "",
    date_cible: t.date_cible ?? "",
    date_realisation: t.date_realisation ?? "",
    non_realise: t.non_realise ?? false,
    statut: t.statut ?? "en_cours",
    retard_jours: t.retard_jours ?? 0,
    lot_concerne: t.lot_concerne ?? "",
    photos: t.photos ?? [],
    realise_pct: t.realise_pct ?? 0,
    fait_le: t.fait_le ?? "",
    important: t.important ?? false,
  });

  const updateTache = (tacheId, field, value) => {
    setTaches(prev => prev.map(t => t.id === tacheId ? { ...t, [field]: value } : t));

    if (readOnly) return;

    // Debounced auto-save
    if (debounceTimers.current[tacheId]) {
      clearTimeout(debounceTimers.current[tacheId]);
    }
    debounceTimers.current[tacheId] = setTimeout(async () => {
      const latest = tachesRef.current.find(t => t.id === tacheId);
      if (!latest) return;
      setAutoSaving(tacheId);
      try {
        if (isOffline) {
          // Queue the update for later sync
          await queueAction({
            method: 'PUT',
            url: `/comptes-rendus/${crId}/taches/${tacheId}`,
            body: _buildPayload(latest),
            meta: { type: 'update_tache', tacheId, crId },
          });
          if (refreshCounts) refreshCounts();
          setSavedRecently(tacheId);
          setTimeout(() => setSavedRecently(prev => prev === tacheId ? null : prev), 2000);
        } else {
          await api.put(`/comptes-rendus/${crId}/taches/${tacheId}`, _buildPayload(latest));
          setSavedRecently(tacheId);
          setTimeout(() => setSavedRecently(prev => prev === tacheId ? null : prev), 2000);
        }
      } catch (e) {
        // If network error, queue it
        if (!navigator.onLine) {
          await queueAction({
            method: 'PUT',
            url: `/comptes-rendus/${crId}/taches/${tacheId}`,
            body: _buildPayload(latest),
            meta: { type: 'update_tache', tacheId, crId },
          });
          if (refreshCounts) refreshCounts();
          setSavedRecently(tacheId);
          setTimeout(() => setSavedRecently(prev => prev === tacheId ? null : prev), 2000);
        } else {
          toast.error("Erreur sauvegarde automatique");
        }
      } finally {
        setAutoSaving(null);
      }
    }, 1200);
  };

  const saveTache = async (tache) => {
    // Clear pending auto-save for this task
    if (debounceTimers.current[tache.id]) {
      clearTimeout(debounceTimers.current[tache.id]);
      delete debounceTimers.current[tache.id];
    }
    setSaving(tache.id);
    try {
      if (isOffline) {
        await queueAction({
          method: 'PUT',
          url: `/comptes-rendus/${crId}/taches/${tache.id}`,
          body: _buildPayload(tache),
          meta: { type: 'save_tache', tacheId: tache.id, crId },
        });
        if (refreshCounts) refreshCounts();
        toast.success("Tâche sauvegardée localement");
      } else {
        await api.put(`/comptes-rendus/${crId}/taches/${tache.id}`, _buildPayload(tache));
        toast.success("Tâche sauvegardée");
        loadTaches(activeLot);
      }
    } catch (e) {
      if (!navigator.onLine) {
        await queueAction({
          method: 'PUT',
          url: `/comptes-rendus/${crId}/taches/${tache.id}`,
          body: _buildPayload(tache),
          meta: { type: 'save_tache', tacheId: tache.id, crId },
        });
        if (refreshCounts) refreshCounts();
        toast.success("Tâche sauvegardée localement");
      } else {
        toast.error("Erreur sauvegarde");
      }
    } finally {
      setSaving(null);
    }
  };

  const deleteTache = async (tacheId) => {
    if (isOffline) {
      toast.info("Suppression impossible hors-ligne");
      return;
    }
    try {
      await api.delete(`/comptes-rendus/${crId}/taches/${tacheId}`);
      toast.success("Tâche supprimée");
      loadTaches(activeLot);
    } catch (e) {
      toast.error("Erreur suppression");
    }
  };

  // Archive with animation
  const archiveTache = async (tacheId) => {
    setArchivingId(tacheId);
    // Wait for animation
    setTimeout(async () => {
      updateTache(tacheId, "statut", "archive");
      setArchivingId(null);
    }, 350);
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadingFor) return;

    if (isOffline || !navigator.onLine) {
      // Store photo locally for later sync
      try {
        await storePhoto({
          tacheId: uploadingFor,
          crId,
          lot: activeLot,
          blob: file,
          filename: file.name,
        });
        // Update local offline photos map for immediate thumbnail display
        const offPhotos = await getPhotosForTask(uploadingFor);
        setOfflinePhotosMap(prev => ({ ...prev, [uploadingFor]: offPhotos }));
        if (refreshCounts) refreshCounts();
        toast.success("Photo enregistrée localement");
      } catch (err) {
        toast.error("Erreur stockage local de la photo");
      }
    } else {
      const formData = new FormData();
      formData.append("file", file);
      try {
        const res = await api.post("/upload/photo", formData);
        const tache = taches.find(t => t.id === uploadingFor);
        if (tache) {
          const photos = [...(tache.photos || []), res.data.url].slice(0, 3);
          updateTache(uploadingFor, "photos", photos);
          await api.put(`/comptes-rendus/${crId}/taches/${uploadingFor}`, { photos });
          toast.success("Photo ajoutée");
          loadTaches(activeLot);
        }
      } catch (err) {
        // Fallback: store locally if upload fails mid-request
        try {
          await storePhoto({
            tacheId: uploadingFor,
            crId,
            lot: activeLot,
            blob: file,
            filename: file.name,
          });
          const offPhotos = await getPhotosForTask(uploadingFor);
          setOfflinePhotosMap(prev => ({ ...prev, [uploadingFor]: offPhotos }));
          if (refreshCounts) refreshCounts();
          toast.info("Photo enregistrée localement (envoi au retour de la connexion)");
        } catch {
          toast.error("Erreur upload photo");
        }
      }
    }
    setUploadingFor(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const getRowClass = (t) => {
    if (t.important) return "bg-amber-500/15 border-l-2 border-l-amber-400";
    if (t.statut === "realise") return "opacity-50 bg-zinc-900/30";
    if (t.statut === "a_refaire") return "bg-red-500/8 border-l-2 border-l-red-500";
    if (t.statut === "archive") return "opacity-50 bg-red-950/20 border-l-2 border-l-red-800";
    if (t.retard_jours > 0 && t.statut !== "realise") return "bg-amber-500/5";
    return "";
  };

  const getBarColor = (t) => {
    if (t.realise_pct >= 100) return "bg-emerald-500";
    if (t.retard_jours > 0 && t.realise_pct < 100) return "bg-red-500";
    if (t.realise_pct > 0) return "bg-orange-500";
    return "bg-zinc-700";
  };

  const isAvancement = activeLot === "avancement_global";
  const isMoa = activeLot === "moa";
  const activeTaches = taches.filter(t => t.statut !== "archive");
  const archivedTaches = taches.filter(t => t.statut === "archive");

  return (
    <div className="space-y-3" data-testid="suivi-lots-panel">
      {/* Hidden file input for photo upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handlePhotoUpload}
      />

      {/* Lot tabs - horizontally scrollable + swipe */}
      <div className="relative">
        <div ref={scrollRef} className="flex gap-1 overflow-x-auto pb-2 scrollbar-thin" data-testid="lots-tabs">
          {LOTS.map(lot => {
            const count = lotCounts[lot.key] || 0;
            return (
              <button
                key={lot.key}
                onClick={() => setActiveLot(lot.key)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap flex items-center gap-1.5 ${
                  activeLot === lot.key
                    ? "bg-orange-500/20 text-orange-400 border border-orange-500/30"
                    : "bg-zinc-900 text-zinc-500 border border-zinc-800 hover:text-zinc-300"
                }`}
                data-testid={`lot-tab-${lot.key}`}
              >
                <span className="hidden sm:inline">{lot.label}</span>
                <span className="sm:hidden">{lot.short}</span>
                {count > 0 && (
                  <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] font-bold leading-none ${
                    activeLot === lot.key ? "bg-orange-500/30 text-orange-300" : "bg-zinc-800 text-zinc-400"
                  }`} data-testid={`lot-count-${lot.key}`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content area with swipe */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-3"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-white font-bold text-sm tracking-wide uppercase">
            {LOTS.find(l => l.key === activeLot)?.label}
          </h3>
          <span className="text-zinc-500 text-xs">
            {activeTaches.length} tâche{activeTaches.length > 1 ? "s" : ""} active{activeTaches.length > 1 ? "s" : ""}
          </span>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="animate-spin text-orange-500" size={24} />
          </div>
        ) : (
          <>
            {/* MOA report tasks (read-only) */}
            {moaTaches.length > 0 && (
              <div className="space-y-2 mb-4">
                {moaTaches.map(mt => (
                  <div key={mt.id} className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 text-sm" data-testid={`moa-report-${mt.id}`}>
                    <div className="flex items-start gap-2">
                      <AlertTriangle size={14} className="text-amber-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="text-amber-400 font-medium text-xs">Action demandée par M.O.A</span>
                        <p className="text-zinc-300 mt-1">{mt.description || "(sans description)"}</p>
                        {mt.date_cible && mt.date_cible.slice(0, 4) >= "2000" && <span className="text-zinc-500 text-xs">Cible : {mt.date_cible}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Task list */}
            {isAvancement ? (
              /* ===== AVANCEMENT GLOBAL ===== */
              <div className="space-y-2">
                {activeTaches.map(t => (
                  <div key={t.id} className={`rounded-lg border border-zinc-800 p-3 space-y-2 transition-all ${getRowClass(t)} ${animatingNew === t.id ? "animate-slide-in-top" : ""} ${archivingId === t.id ? "animate-slide-out-right" : ""}`} data-testid={`tache-${t.id}`}>
                    <div className="flex gap-2">
                      <Input
                        value={t.description}
                        onChange={e => updateTache(t.id, "description", e.target.value)}
                        placeholder="Décrivez la tâche ou dictez-la avec le micro..."
                        className="input-dark h-8 text-sm flex-1"
                        readOnly={readOnly}
                        data-testid={`tache-desc-${t.id}`}
                      />
                      {!readOnly && (
                        <VoiceButton
                          onTranscript={(text) => {
                            const current = tachesRef.current.find(x => x.id === t.id);
                            const base = current?.description || "";
                            const sep = base && !base.endsWith(" ") ? " " : "";
                            updateTache(t.id, "description", base + sep + text);
                          }}
                        />
                      )}
                      <Input
                        type="date"
                        value={t.fait_le}
                        onChange={e => updateTache(t.id, "fait_le", e.target.value)}
                        className="input-dark h-8 text-sm w-36"
                        title="Fait le"
                        readOnly={readOnly}
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2 flex-1">
                        <span className="text-zinc-500 text-xs w-14">Réalisé</span>
                        <input
                          type="range"
                          min="0" max="100"
                          value={t.realise_pct}
                          onChange={e => updateTache(t.id, "realise_pct", parseInt(e.target.value))}
                          className="flex-1 accent-orange-500 h-1.5"
                          disabled={readOnly}
                        />
                        <span className="text-white text-xs font-mono w-10 text-right">{t.realise_pct}%</span>
                      </div>
                      <div className="text-zinc-500 text-xs">
                        Reste: <span className="text-white font-mono">{100 - t.realise_pct}%</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-zinc-500 text-xs">Retard</span>
                        <Input
                          type="number" min="0"
                          value={t.retard_jours}
                          onChange={e => updateTache(t.id, "retard_jours", parseInt(e.target.value) || 0)}
                          className="input-dark h-7 text-xs w-14 text-center"
                          readOnly={readOnly}
                        />
                        <span className="text-zinc-500 text-xs">j</span>
                      </div>
                    </div>
                    {/* Progress bar */}
                    <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${getBarColor(t)}`} style={{ width: `${t.realise_pct}%` }} />
                    </div>
                    <div className="flex justify-between items-center gap-1">
                      <button
                        onClick={() => updateTache(t.id, "important", !t.important)}
                        disabled={readOnly}
                        className={`flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded transition-colors ${
                          t.important
                            ? "bg-amber-500/20 text-amber-400 border border-amber-500/40"
                            : "text-zinc-600 hover:text-amber-400"
                        }`}
                        data-testid={`tache-important-${t.id}`}
                      >
                        <Flag size={10} fill={t.important ? "currentColor" : "none"} />
                        {t.important ? "Important" : "Marquer important"}
                      </button>
                      <div className="flex items-center gap-1">
                      {autoSaving === t.id && (
                        <span className="text-orange-400 text-[10px] flex items-center gap-1 mr-1" data-testid={`autosave-indicator-${t.id}`}>
                          <Loader2 size={10} className="animate-spin" /> Sauvegarde...
                        </span>
                      )}
                      {savedRecently === t.id && !autoSaving && (
                        <span className="text-emerald-400 text-[10px] flex items-center gap-1 mr-1 animate-fade-in" data-testid={`saved-indicator-${t.id}`}>
                          <CheckCircle2 size={10} /> Sauvegardé
                        </span>
                      )}
                      {!readOnly && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => saveTache(t)} className="h-7 px-2 text-xs border-zinc-700 text-zinc-400 hover:text-orange-400 hover:border-orange-500/30 gap-1" data-testid={`save-tache-${t.id}`}>
                            {saving === t.id ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                            <span className="hidden sm:inline">Sauver</span>
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setTaskToDelete(t.id)} className="h-7 px-2 text-xs text-zinc-400 hover:text-red-400" data-testid={`delete-tache-${t.id}`}>
                            <Trash2 size={12} />
                          </Button>
                        </>
                      )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* ===== SUIVI PAR LOT (onglets 2-14) ===== */
              <div className="space-y-2">
                {activeTaches.map(t => {
                  const statusInfo = STATUTS.find(s => s.value === t.statut) || STATUTS[0];
                  const dateCible = t.date_cible && t.date_cible.slice(0, 4) >= "2000" ? t.date_cible : "";
                  const dateReal = t.date_realisation && t.date_realisation.slice(0, 4) >= "2000" ? t.date_realisation : "";
                  return (
                  <div key={t.id}
                    className={`rounded-lg border border-zinc-800 p-3 space-y-2 transition-all ${getRowClass(t)} ${animatingNew === t.id ? "animate-slide-in-top" : ""} ${archivingId === t.id ? "animate-slide-out-right" : ""}`}
                    data-testid={`tache-${t.id}`}
                  >
                    {/* Row 1: Description + Voice */}
                    <div className="flex gap-2">
                      <textarea
                        value={t.description}
                        onChange={e => updateTache(t.id, "description", e.target.value)}
                        placeholder="Décrivez la tâche ou dictez-la avec le micro..."
                        rows={2}
                        className="flex-1 bg-zinc-800/50 border border-zinc-700 rounded-md px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-orange-500/50 resize-none"
                        readOnly={readOnly}
                        data-testid={`tache-desc-${t.id}`}
                      />
                      {!readOnly && (
                        <VoiceButton
                          onTranscript={(text) => {
                            const current = tachesRef.current.find(x => x.id === t.id);
                            const base = current?.description || "";
                            const sep = base && !base.endsWith(" ") ? " " : "";
                            updateTache(t.id, "description", base + sep + text);
                          }}
                        />
                      )}
                    </div>
                    {/* Row 2: Dates + Status */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <div className="space-y-0.5">
                        <label className="text-zinc-600 text-[10px] uppercase">À faire pour le</label>
                        {dateCible || !readOnly ? (
                          <Input type="date" value={dateCible} onChange={e => updateTache(t.id, "date_cible", e.target.value)} className="input-dark h-7 text-xs" readOnly={readOnly} />
                        ) : (
                          <div className="h-7 flex items-center px-2 text-xs text-zinc-600">—</div>
                        )}
                      </div>
                      <div className="space-y-0.5">
                        <label className="text-zinc-600 text-[10px] uppercase">Réalisé le</label>
                        {dateReal || !readOnly ? (
                          <Input type="date" value={dateReal} onChange={e => updateTache(t.id, "date_realisation", e.target.value)} className="input-dark h-7 text-xs" readOnly={readOnly} />
                        ) : (
                          <div className="h-7 flex items-center px-2 text-xs text-zinc-600">—</div>
                        )}
                      </div>
                      <div className="space-y-0.5">
                        <label className="text-zinc-600 text-[10px] uppercase">Statut</label>
                        <select
                          value={t.statut}
                          onChange={e => {
                            if (e.target.value === "archive") {
                              archiveTache(t.id);
                            } else {
                              updateTache(t.id, "statut", e.target.value);
                            }
                          }}
                          disabled={readOnly}
                          className="w-full bg-zinc-800 border border-zinc-700 rounded-md px-2 h-7 text-xs focus:outline-none focus:border-orange-500/50"
                          style={{ color: statusInfo.selectColor }}
                          data-testid={`tache-statut-${t.id}`}
                        >
                          {STATUTS.map(s => (
                            <option key={s.value} value={s.value} style={{ color: s.selectColor }}>{s.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-0.5">
                        <label className="text-zinc-600 text-[10px] uppercase">Retard</label>
                        <div className={`h-7 flex items-center px-2 rounded-md text-xs font-mono ${
                          t.retard_jours > 0 ? "bg-red-500/15 text-red-400 border border-red-500/20" : "bg-zinc-800 text-zinc-500 border border-zinc-700"
                        }`}>
                          {t.retard_jours > 0 ? `${t.retard_jours}j retard` : "0j"}
                        </div>
                      </div>
                    </div>
                    {/* Row 3: Non réalisé + MOA lot_concerne + Photos + Actions */}
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-1.5 text-xs text-zinc-400">
                          <Checkbox
                            checked={t.non_realise}
                            onCheckedChange={v => updateTache(t.id, "non_realise", !!v)}
                            disabled={readOnly}
                          />
                          Non réalisé
                        </label>
                        {isMoa && (
                          <div className="flex items-center gap-1">
                            <span className="text-zinc-600 text-[10px]">Lot :</span>
                            <select
                              value={t.lot_concerne || ""}
                              onChange={e => updateTache(t.id, "lot_concerne", e.target.value)}
                              disabled={readOnly}
                              className="bg-zinc-800 border border-zinc-700 rounded px-1.5 h-6 text-[10px] text-white focus:outline-none focus:border-orange-500/50"
                              data-testid={`tache-lot-concerne-${t.id}`}
                            >
                              <option value="">—</option>
                              {LOTS.filter(l => l.key !== "avancement_global" && l.key !== "moa").map(l => (
                                <option key={l.key} value={l.key}>{l.label}</option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          onClick={() => updateTache(t.id, "important", !t.important)}
                          disabled={readOnly}
                          className={`flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded transition-colors ${
                            t.important
                              ? "bg-amber-500/20 text-amber-400 border border-amber-500/40"
                              : "text-zinc-600 hover:text-amber-400"
                          }`}
                          data-testid={`tache-important-${t.id}`}
                        >
                          <Flag size={10} fill={t.important ? "currentColor" : "none"} />
                          {t.important ? "Important" : "Important"}
                        </button>
                      <div className="flex items-center gap-1">
                        {/* Auto-save indicator */}
                        {autoSaving === t.id && (
                          <span className="text-orange-400 text-[10px] flex items-center gap-1">
                            <Loader2 size={10} className="animate-spin" />
                          </span>
                        )}
                        {savedRecently === t.id && !autoSaving && (
                          <span className="text-emerald-400 text-[10px] flex items-center gap-1 animate-fade-in" data-testid={`saved-check-${t.id}`}>
                            <CheckCircle2 size={10} />
                          </span>
                        )}
                        {/* Photos */}
                        {(t.photos || []).map((url, pi) => (
                          <div key={pi} className="w-8 h-8 rounded border border-zinc-700 overflow-hidden">
                            <img src={`${process.env.REACT_APP_BACKEND_URL}${url}`} alt="" className="w-full h-full object-cover" />
                          </div>
                        ))}
                        {/* Offline pending photos */}
                        {(offlinePhotosMap[t.id] || []).map((op) => (
                          <OfflinePhotoThumb key={`off-${op.id}`} photo={op} />
                        ))}
                        {!readOnly && ((t.photos || []).length + (offlinePhotosMap[t.id] || []).length) < 3 && (
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => { setUploadingFor(t.id); fileInputRef.current?.click(); }}
                            className="h-8 w-8 p-0 text-zinc-500 hover:text-orange-400"
                            title="Ajouter photo"
                          >
                            <Camera size={14} />
                          </Button>
                        )}
                        {!readOnly && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => saveTache(t)} className="h-7 px-2 text-xs border-zinc-700 text-zinc-400 hover:text-orange-400 hover:border-orange-500/30 gap-1" data-testid={`save-tache-${t.id}`}>
                              {saving === t.id ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                              <span className="hidden sm:inline">Sauver</span>
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setTaskToDelete(t.id)} className="h-7 px-2 text-xs text-zinc-400 hover:text-red-400" data-testid={`delete-tache-${t.id}`}>
                              <Trash2 size={12} />
                            </Button>
                          </>
                        )}
                      </div>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}

            {/* Archived tasks (collapsed) */}
            {archivedTaches.length > 0 && (
              <details className="mt-4">
                <summary className="text-zinc-600 text-xs cursor-pointer hover:text-zinc-400 flex items-center gap-1">
                  <XCircle size={12} /> {archivedTaches.length} tâche{archivedTaches.length > 1 ? "s" : ""} non réalisée{archivedTaches.length > 1 ? "s" : ""}
                </summary>
                <div className="space-y-2 mt-2">
                  {archivedTaches.map(t => (
                    <div key={t.id} className="rounded-lg border border-zinc-800/50 p-3 opacity-40" data-testid={`tache-archived-${t.id}`}>
                      <div className="flex items-center justify-between">
                        <p className="text-zinc-500 text-sm line-through">{t.description || "(sans description)"}</p>
                        <Button size="sm" variant="ghost" onClick={() => { updateTache(t.id, "statut", "en_cours"); saveTache({...t, statut: "en_cours"}); }} className="h-6 px-2 text-[10px] text-zinc-600 hover:text-emerald-400" disabled={readOnly}>
                          Restaurer
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            )}

            {/* Empty state */}
            {activeTaches.length === 0 && moaTaches.length === 0 && (
              <div className="text-center py-8" data-testid="empty-lot-state">
                <p className="text-zinc-500 text-sm mb-1">Aucune tâche pour ce lot.</p>
                {!readOnly && (
                  <p className="text-zinc-600 text-xs">Cliquez sur + Ajouter une tâche ou dictez directement.</p>
                )}
              </div>
            )}

            {/* Add task button */}
            {!readOnly && (
              <Button onClick={addTache} variant="outline" className="w-full border-dashed border-zinc-700 text-zinc-400 hover:text-orange-400 hover:border-orange-500/30 gap-1.5" data-testid="add-tache-btn">
                <Plus size={14} /> Ajouter une tâche
              </Button>
            )}
          </>
        )}
      </div>

      {/* Dialog confirmation suppression */}
      <Dialog open={!!taskToDelete} onOpenChange={(open) => !open && setTaskToDelete(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm mx-4">
          <DialogHeader>
            <DialogTitle className="text-white text-base flex items-center gap-2">
              <AlertTriangle size={18} className="text-red-400" />
              Supprimer cette tâche ?
            </DialogTitle>
            <DialogDescription className="text-zinc-400 text-sm pt-2">
              Cette action est irréversible. La tâche et ses photos associées seront définitivement supprimées.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setTaskToDelete(null)}
              className="flex-1 border-zinc-700 text-zinc-400"
              data-testid="cancel-delete-tache-btn"
            >
              Annuler
            </Button>
            <Button
              onClick={() => { deleteTache(taskToDelete); setTaskToDelete(null); }}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white gap-2"
              data-testid="confirm-delete-tache-btn"
            >
              <Trash2 size={14} />
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuiviLotsPanel;
